/**
 * TierAccessGate Component
 *
 * Purpose: Enforce tier-based access control
 *
 * Rules:
 * - Discover tier: ONLY /decouvrir access (redirect to /decouvrir)
 * - All other tiers: Full access to dashboard, subsidies, profiles
 *
 * Usage:
 * <TierAccessGate requireFullAccess>
 *   <Dashboard />
 * </TierAccessGate>
 */

import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserTierFromContext } from '@/contexts/SubscriptionContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { logger } from '@/utils/logger';

interface TierAccessGateProps {
  children: React.ReactNode;
  requireFullAccess?: boolean; // If true, redirect Discover users
  allowedForDiscover?: boolean; // If true, Discover users can access
}

export const TierAccessGate: React.FC<TierAccessGateProps> = ({
  children,
  requireFullAccess = true,
  allowedForDiscover = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  // PERF FIX: Use consolidated context instead of separate hook
  const { tier, isLoading, hasFullAccess } = useUserTierFromContext();

  // PERF DIAGNOSTIC: Track when TierAccessGate renders
  React.useEffect(() => {
    console.log(`🎫 [PERF] TierAccessGate rendered at ${new Date().toISOString().split('T')[1]} (isLoading: ${isLoading}, tier: ${tier})`);
  }, [isLoading, tier]);

  useEffect(() => {
    if (isLoading) return;

    // If page requires full access and user has Discover tier -> redirect
    if (requireFullAccess && !hasFullAccess && tier === 'discover') {
      logger.debug('🚫 Discover user trying to access restricted page, redirecting to /decouvrir');

      toast({
        title: 'Accès Limité',
        description: 'Votre forfait Découvrir donne accès uniquement au chatbot et au calculateur. Passez au forfait Solo pour un accès complet.',
        variant: 'default',
      });

      navigate('/decouvrir', { replace: true });
    }
  }, [tier, isLoading, hasFullAccess, requireFullAccess, navigate]);

  // PERF FIX: Don't block rendering while loading tier
  // Most authenticated users have full access, so show content optimistically
  // and redirect only if we confirm they're on discover tier
  // This eliminates the 4+ second loading state delay

  // If Discover user on restricted page, show upgrade prompt
  if (requireFullAccess && tier === 'discover' && !hasFullAccess) {
    return (
      <div className="container mx-auto p-6 max-w-2xl mt-12">
        <Card className="border-primary/20">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">Accès Restreint</CardTitle>
            <CardDescription className="text-base mt-2">
              Votre forfait <strong>Découvrir</strong> donne accès au chatbot et au calculateur uniquement.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted/50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                Ce que vous pouvez faire avec Découvrir :
              </h3>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>✓ Utiliser le chatbot pour trouver des subventions</li>
                <li>✓ Calculer votre éligibilité</li>
                <li>✓ Obtenir des recommandations personnalisées</li>
              </ul>
            </div>

            <div className="bg-primary/5 p-4 rounded-lg border border-primary/10">
              <h3 className="font-semibold mb-2">
                Passez au forfait Solo pour débloquer :
              </h3>
              <ul className="space-y-1 text-sm">
                <li>✓ Tableau de bord complet</li>
                <li>✓ Gestion de profils d'entreprise</li>
                <li>✓ Accès à toutes les subventions</li>
                <li>✓ Suivi des candidatures</li>
                <li>✓ Calendrier des échéances</li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => navigate('/decouvrir')}
                variant="outline"
                className="flex-1"
              >
                Retour au Chatbot
              </Button>
              <Button
                onClick={() => navigate('/pricing')}
                className="flex-1"
              >
                Voir les Forfaits
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User has appropriate access, render children
  return <>{children}</>;
};
